/**
 * 체험단 공개 API 클라이언트 (참여자용)
 *
 * Cafe24 회원 OAuth 인증 기반 공개 API
 * - shop_no 단위 멀티테넌시
 * - JWT 토큰 인증
 */

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api-dev.1bpluschool.com'
const PUBLIC_API_BASE = `${API_BASE_URL}/v2/seeding/public`

// 세션 토큰 저장 키
const SESSION_TOKEN_KEY = 'seeding_session_token'
const PHONE_SESSION_TOKEN_KEY = 'seeding_phone_session_token'

// ==================== Types ====================

// SNS 계정 정보
export interface SnsAccount {
  channel: string
  account: string
  followers?: string // 범위: "under_1k", "1k_5k", "5k_10k", "10k_50k", "50k_100k", "over_100k"
}

// 다차수 미션 차수 정의
export interface MissionRound {
  round: number
  title: string
  guideline?: string
  guideline_files?: string[] // 가이드라인 첨부파일 URL 목록
  deadline_at: string // ISO datetime
  mission_type?: 'media' | 'link' | 'both' // 차수별 제출 유형
}

// 공개 캠페인 응답
export interface PublicCampaignResponse {
  id: string
  mall_id: string
  shop_no?: number // shop_no (short URL 라우트에서 필요)
  short_code?: string // Short URL 코드
  shop_name?: string // 쇼핑몰 이름 (로그인 안내에 사용)
  meta_pixel_id?: string // Meta Pixel ID (브라우저 이벤트 발송용)
  title: string
  description?: string
  notices?: string  // 주의사항
  thumbnail_url?: string
  detail_images?: string[]
  product_name: string
  product_image_url?: string
  product_price?: number
  product_url?: string
  recruit_count: number
  recruit_start_at: string
  recruit_end_at: string
  campaign_end_at?: string | null  // 캠페인 종료일
  selection_announce_at?: string // 선정 발표일
  application_count: number
  social_media_channels?: Record<string, 'hidden' | 'optional' | 'required'> | null  // 채널별 visibility
  mission_deadline_at?: string
  mission_rounds?: MissionRound[] // 미션 차수 설정
  // 수집 항목 설정 (필드별 hidden/optional/required, sns는 social_media_channels에서 채널별 관리)
  field_config: {
    email: 'hidden' | 'optional' | 'required'
    gender: 'hidden' | 'optional' | 'required'
    birthdate: 'hidden' | 'optional' | 'required'
    address: 'hidden' | 'optional' | 'required'
    reason: 'hidden' | 'optional' | 'required'
  }
  // 추가 질문
  extra_questions?: {
    id: string
    type: 'text' | 'textarea' | 'select' | 'checkbox'
    label: string
    required: boolean
    options?: string[]
    order: number
  }[]
  status: string
  is_recruiting: boolean
  login_required: boolean
  delivery_method?: string // "cafe24_order" | "self_delivery"
  auto_coupon?: boolean // 쿠폰 자동 생성 여부
  product_coupon_name?: string | null // 상품 할인 쿠폰 이름 (deprecated)
  shipping_coupon_name?: string | null // 배송비 쿠폰 이름
  // 복수 옵션 지원
  allowed_variants?: Array<{ variant_code: string; label: string; additional_amount: number; price: number }> | null
  variant_coupons?: Array<{ price: number; coupon_no: string; coupon_name: string; variant_codes: string[] }> | null
  shop_domain?: string | null // 쇼핑몰 도메인 (회원가입 링크용)
  contact?: SeedingContact | null // 운영자(고객사) 문의처 — 참여자 노출용
}

// 운영자(고객사) 문의처 — Shop 단위로 등록, 참여자에게 노출
export interface SeedingContact {
  phone?: string | null // 전화번호
  kakao_url?: string | null // 카카오톡 채널·오픈채팅 URL
  email?: string | null // 이메일
}

// 주문/배송 상태 응답
export interface OrderStatusResponse {
  delivery_method: string
  has_order: boolean
  member_id_required?: boolean
  order_date?: string
  shipping_status?: string
  shipping_status_label?: string
  message?: string
  error?: string
  // 자체배송(self_delivery) 송장 정보 — 운영자 입력, 참여자 조회용
  shipping_courier?: string | null // 택배사 코드 (lib/couriers.ts 참조)
  shipping_tracking_number?: string | null // 송장번호
  shipping_registered_at?: string | null // 송장 등록 시각 (ISO)
}

// 체험단 신청 요청
export interface SeedingApplicationCreateRequest {
  name: string
  phone: string
  email?: string
  gender?: string // male, female
  birthdate?: string // YYYY-MM-DD
  zipcode?: string
  address1?: string
  address2?: string
  sns_accounts?: SnsAccount[]
  application_reason?: string
  extra_question_answers?: { question_id: string; value: string | string[] }[]
  agreed_privacy: boolean // 개인정보 수집·이용 동의
  agreed_third_party: boolean // 개인정보 제3자 제공 동의
}

// 미션 제출 응답
export interface MissionSubmissionResponse {
  id: string
  application_id: string
  round_number?: number // 다차수 미션 차수 번호
  mission_type: string
  mission_url?: string
  mission_content?: string
  media_urls?: string[]
  status: string
  rejection_reason?: string // 반려 사유
  created_at: string
  updated_at?: string
}

// 내 신청 응답
export interface MyApplicationResponse {
  id: string
  campaign_id: string
  campaign_title: string
  campaign_thumbnail_url?: string
  product_name: string
  status: string
  selected_at?: string
  selection_announce_at?: string // 선정 발표일 (미선정 표시 분기용)
  // 쿠폰 관련
  cafe24_member_id?: string | null
  coupon_issued_at?: string | null
  selected_variant_code?: string | null
  submissions: MissionSubmissionResponse[]
  // 개인정보 (내 신청 확인 페이지용)
  name: string
  email?: string | null
  zipcode?: string | null
  address1?: string | null
  address2?: string | null
  sns_accounts?: SnsAccount[] | null
  application_reason?: string | null
  extra_question_answers?: { question_id: string; value: string | string[] }[] | null
  gender?: string | null
  birthdate?: string | null
  created_at: string
}

// 미션 제출 요청
export interface MissionSubmissionCreateRequest {
  mission_type: string
  mission_url?: string
  mission_content?: string
  media_urls?: string[] // 업로드된 미디어 파일 URL (GCS)
  round_number?: number // 다차수 캠페인 차수 번호
}

// 업로드 URL 요청
export interface UploadUrlRequest {
  file_type: 'image' | 'video'
  content_type: string
  original_filename: string
  file_size: number
}

// 업로드 URL 응답
export interface UploadUrlResponse {
  upload_url: string
  public_url: string
  blob_name: string
  expires_in: number
}

// SMS 인증 세션 응답
export interface PhoneSessionResponse {
  session_token: string
  phone: string
}

// OAuth 콜백 응답
export interface AuthCallbackResponse {
  session_token: string
  user_identifier: string
  mall_id: string
  shop_no: number
  return_url?: string // 팝업 차단 시 돌아갈 URL
  prefill?: {
    name: string
    phone: string
    email?: string
    zipcode?: string
    address1?: string
    address2?: string
    sns_accounts?: SnsAccount[]
  }
}

// ==================== 세션 관리 ====================

/**
 * 세션 토큰 저장
 */
export function saveSessionToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token)
}

/**
 * 세션 토큰 조회
 */
export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY)
}

/**
 * 세션 토큰 삭제
 */
export function clearSessionToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY)
}

/**
 * 로그인 여부 확인
 */
export function isLoggedIn(): boolean {
  return !!getSessionToken()
}

// ==================== Phone Session 관리 ====================

export function savePhoneSessionToken(token: string): void {
  localStorage.setItem(PHONE_SESSION_TOKEN_KEY, token)
}

export function getPhoneSessionToken(): string | null {
  return localStorage.getItem(PHONE_SESSION_TOKEN_KEY)
}

export function clearPhoneSessionToken(): void {
  localStorage.removeItem(PHONE_SESSION_TOKEN_KEY)
}

export function isPhoneLoggedIn(): boolean {
  return !!getPhoneSessionToken()
}

// ==================== API Client ====================

// 공통 fetch 함수 (인증 포함)
async function fetchPublic(url: string, options: RequestInit = {}) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // 세션 토큰이 있으면 Authorization 헤더 추가 (OAuth 우선, phone session fallback)
  const token = getSessionToken() || getPhoneSessionToken()
  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    // 401 에러 시 만료된 토큰 삭제
    if (response.status === 401) {
      clearSessionToken()
    }
    const error = await response
      .json()
      .catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || error.message || `Request failed: ${response.status}`)
  }

  return response.json()
}

export const seedingPublicApi = {
  // ==================== OAuth 인증 ====================

  /**
   * OAuth 인증 URL 생성
   *
   * State에 mall_id, shop_no가 포함되어 단일 redirect_uri 사용 가능.
   * redirect_uri는 서버에서 설정된 고정 URL 사용.
   * return_url은 팝업 차단 시 콜백에서 돌아갈 URL (Optional).
   */
  getAuthUrl: async (
    mallId: string,
    shopNo: number,
    returnUrl?: string,
  ): Promise<{ auth_url: string; state: string }> => {
    const params = new URLSearchParams()
    if (returnUrl) params.append('return_url', returnUrl)
    const query = params.toString() ? `?${params}` : ''
    return fetchPublic(`${PUBLIC_API_BASE}/${mallId}/${shopNo}/auth/url${query}`)
  },

  /**
   * OAuth 콜백 처리 (통합 엔드포인트)
   *
   * State에서 mall_id, shop_no를 파싱하므로 URL에 포함 불필요.
   */
  handleAuthCallback: async (
    code: string,
    state: string,
  ): Promise<AuthCallbackResponse> => {
    const queryParams = new URLSearchParams()
    queryParams.append('code', code)
    queryParams.append('state', state)
    const result = await fetchPublic(
      `${PUBLIC_API_BASE}/auth/callback?${queryParams}`,
    )

    // 세션 토큰 자동 저장
    if (result.session_token) {
      saveSessionToken(result.session_token)
    }

    return result
  },

  // 현재 로그인 사용자 정보
  getMe: async (): Promise<{ logged_in: boolean; user_identifier: string; mall_id: string; shop_no: number }> => {
    return fetchPublic(`${PUBLIC_API_BASE}/auth/me`)
  },

  // ==================== Setup Code (선정 시 비밀번호 설정) ====================

  /**
   * Password setup code 유효성 검증
   *
   * 선정 알림톡의 링크에 포함된 code 파라미터를 검증.
   */
  verifySetupCode: async (
    code: string,
  ): Promise<{
    valid: boolean
    phone?: string
    campaign_title?: string
    reason?: string
  }> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/auth/setup-code/validate?code=${encodeURIComponent(code)}`,
    )
  },

  /**
   * Password setup code로 비밀번호 설정
   *
   * code 자체가 인증 역할. 성공 시 member_session_token 발급.
   */
  setPasswordWithCode: async (
    code: string,
    password: string,
  ): Promise<{ session_token: string; phone: string; has_password: boolean }> => {
    const result = await fetchPublic(
      `${PUBLIC_API_BASE}/auth/setup-code/set-password`,
      {
        method: 'POST',
        body: JSON.stringify({ code, password }),
      },
    )

    // 토큰을 phone session 저장소에 저장
    if (result.session_token) {
      savePhoneSessionToken(result.session_token)
    }

    return result
  },

  // ==================== 멤버 로그인 (비밀번호) ====================

  /**
   * 비밀번호 로그인 (shop-scoped)
   *
   * 성공 시 member_session_token을 phone session 저장소에 저장.
   */
  memberLogin: async (
    mallId: string,
    shopNo: number,
    phone: string,
    password: string,
  ): Promise<{ session_token: string; phone: string; has_password: boolean }> => {
    const result = await fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/auth/member/login`,
      {
        method: 'POST',
        body: JSON.stringify({ phone, password }),
      },
    )

    // 토큰을 phone session 저장소에 통합 저장
    if (result.session_token) {
      savePhoneSessionToken(result.session_token)
    }

    return result
  },

  /**
   * 비밀번호 설정 (shop-scoped)
   *
   * 성공 시 member_session_token으로 교체 저장.
   */
  setPassword: async (
    mallId: string,
    shopNo: number,
    password: string,
    campaignId: string,
  ): Promise<{ session_token: string; phone: string; has_password: boolean }> => {
    const result = await fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/auth/member/set-password`,
      {
        method: 'POST',
        body: JSON.stringify({ password, campaign_id: campaignId }),
      },
    )

    // 새 member_session_token으로 교체
    if (result.session_token) {
      savePhoneSessionToken(result.session_token)
    }

    return result
  },

  // ==================== SMS 인증 (비회원용) ====================

  // SMS 인증코드 발송
  sendPhoneCode: async (phone: string): Promise<{ message: string }> => {
    return fetchPublic(`${PUBLIC_API_BASE}/auth/phone/send`, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    })
  },

  // SMS 인증코드 확인 → phone_session_token 발급
  verifyPhoneCode: async (
    phone: string,
    code: string,
  ): Promise<PhoneSessionResponse> => {
    const result = await fetchPublic(`${PUBLIC_API_BASE}/auth/phone/verify`, {
      method: 'POST',
      body: JSON.stringify({ phone, code }),
    })

    // phone session 토큰 자동 저장
    if (result.session_token) {
      savePhoneSessionToken(result.session_token)
    }

    return result
  },

  // ==================== 회원가입 안내 알림톡 ====================

  // 회원가입 안내 알림톡 발송 (fbc는 알림톡 링크에 보존용)
  notifySignup: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
    phone: string,
    fbc?: string,
  ): Promise<{ message: string }> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}/notify-signup`,
      {
        method: 'POST',
        body: JSON.stringify({ phone, fbc }),
      },
    )
  },

  // ==================== 캠페인 조회 ====================

  // 공개 캠페인 상세 조회
  getCampaign: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
  ): Promise<PublicCampaignResponse> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}`,
    )
  },

  // short_code로 캠페인 조회 (참여자용 short URL)
  resolveShortCode: async (
    shortCode: string,
  ): Promise<PublicCampaignResponse> => {
    return fetchPublic(`${PUBLIC_API_BASE}/c/${shortCode}`)
  },

  // ==================== 체험단 신청 ====================

  // 체험단 신청 (로그인 필수)
  applyToCampaign: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
    data: SeedingApplicationCreateRequest,
  ): Promise<MyApplicationResponse> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}/apply`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  },

  // ==================== 내 신청 조회 ====================

  // 캠페인별 내 신청 조회 - 전화번호 인증 (phone session token 필요)
  getMyApplicationByCampaignPhone: async (
    campaignId: string,
  ): Promise<MyApplicationResponse> => {
    return fetchPublic(`${PUBLIC_API_BASE}/my/phone/campaigns/${campaignId}`)
  },

  // ==================== 미션 제출 ====================

  // 비회원 미션 제출 (phone session token 필요)
  submitSeedingByPhone: async (
    applicationId: string,
    data: MissionSubmissionCreateRequest,
  ): Promise<MissionSubmissionResponse> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/applications/${applicationId}/submit/phone`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  },

  // 미션 제출 수정 (phone session token 필요, submitted 상태만)
  editSubmissionByPhone: async (
    applicationId: string,
    submissionId: string,
    data: { media_urls?: string[]; mission_url?: string },
  ): Promise<MissionSubmissionResponse> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/applications/${applicationId}/submissions/${submissionId}/phone`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
  },

  // ==================== Cafe24 회원 검증 ====================

  /**
   * Cafe24 회원 ID 검증
   *
   * 참여자가 입력한 Cafe24 로그인 ID가 본인 소유인지 확인.
   * 이름 불일치 시 name_mismatch 경고를 반환하며, force=true로 재요청하면 건너뜀.
   */
  verifyCafe24Member: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
    memberId: string,
    force: boolean = false,
  ): Promise<{
    success: boolean
    name_mismatch: boolean
    cafe24_member_id?: string
    customer_name?: string
    applicant_name?: string
    coupon_issued?: boolean
    coupon_error?: string | null
    message: string
  }> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}/verify-cafe24-member`,
      {
        method: 'POST',
        body: JSON.stringify({ member_id: memberId, force }),
      },
    )
  },

  /**
   * 쿠폰 발급 요청 (옵션 선택 후)
   *
   * 참여자가 옵션을 선택한 후 쿠폰 발급을 요청한다.
   * cafe24_member_id 검증이 완료된 상태에서만 호출 가능.
   */
  requestCoupon: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
    variantCode: string | null,
  ): Promise<{
    success: boolean
    coupon_issued: boolean
    coupon_name?: string
    selected_variant_code?: string | null
    message: string
  }> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}/request-coupon`,
      {
        method: 'POST',
        body: JSON.stringify({ variant_code: variantCode }),
      },
    )
  },

  // ==================== 파일 업로드 ====================

  /**
   * 업로드 URL 요청 (GCS Signed URL)
   *
   * 참여자가 이미지/영상을 직접 GCS에 업로드하기 위한 Signed URL을 발급받는다.
   */
  getUploadUrl: async (
    applicationId: string,
    data: UploadUrlRequest,
  ): Promise<UploadUrlResponse> => {
    return fetchPublic(
      `${PUBLIC_API_BASE}/applications/${applicationId}/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  },

  /**
   * 파일을 GCS에 직접 업로드
   *
   * 1. getUploadUrl로 Signed URL 발급받기
   * 2. 해당 URL로 PUT 요청하여 파일 업로드
   * 3. public_url 반환
   */
  uploadFile: async (
    applicationId: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<string> => {
    // 파일 타입 결정
    const fileType = file.type.startsWith('video/') ? 'video' : 'image'

    // Signed URL 발급
    const { upload_url, public_url } = await seedingPublicApi.getUploadUrl(
      applicationId,
      {
        file_type: fileType,
        content_type: file.type,
        original_filename: file.name,
        file_size: file.size,
      },
    )

    // GCS에 직접 업로드 (XMLHttpRequest for progress tracking)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(public_url)
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'))
      })

      xhr.open('PUT', upload_url)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  },

  // ==================== 주문/배송 상태 ====================

  // 주문 상태 조회
  getOrderStatus: async (
    mallId: string,
    shopNo: number,
    campaignId: string,
  ): Promise<OrderStatusResponse> => {
    return await fetchPublic(
      `${PUBLIC_API_BASE}/${mallId}/${shopNo}/campaigns/${campaignId}/my-order-status`,
      { method: 'GET' },
    )
  },
}
